// v3.18.0 — Tableau de bord Diaspora : vue spécialisée du moteur unique
// "Demande & Prise en charge" (aya_requests). 4 blocs : ❤️ Famille,
// 💼 Business, 🏠 Installation, 🕊️ Solidarité.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const API = process.env.REACT_APP_BACKEND_URL || "";
const fmt = (n) => (Number.isFinite(Number(n)) ? `${Number(n).toLocaleString("fr-FR")} F` : "");

const STATUS_LABEL = {
  created: "En attente", accepted: "Acceptée", paid: "Payée",
  delivered: "Livrée", completed: "Terminée", cancelled: "Annulée",
};
const STATUS_COLOR = {
  created: "#FBBF24", accepted: "#60A5FA", paid: "#34D399",
  delivered: "#34D399", completed: "#6EE7B7", cancelled: "#F87171",
};
const TYPE_EMOJI = {
  panier: "🧺", facture: "🧾", projet: "💼",
  investissement: "📈", installation: "🏠", solidarite: "🕊️",
};

function StatusChip({ status }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 999,
      color: STATUS_COLOR[status] || "#ccc",
      border: `1px solid ${STATUS_COLOR[status] || "#ccc"}44`,
      background: `${STATUS_COLOR[status] || "#ccc"}18`,
      whiteSpace: "nowrap",
    }}>{STATUS_LABEL[status] || status}</span>
  );
}

function RequestRow({ r }) {
  return (
    <div data-testid={`diaspora-request-${r.request_id}`} style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.06)",
    }}>
      <span style={{ fontSize: 16 }}>{TYPE_EMOJI[r.type] || "📄"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
        <div style={{ fontSize: 10.5, opacity: 0.55 }}>
          {r.beneficiary?.name ? `${r.beneficiary.name} · ` : ""}{fmt(r.amount_fcfa)}
          {Number.isFinite(r.progress_pct) && r.progress_pct !== null && r.status !== "completed" ? ` · ${r.progress_pct}%` : ""}
        </div>
        {Number.isFinite(r.progress_pct) && r.progress_pct !== null && (
          <div style={{ marginTop: 4, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.09)", overflow: "hidden" }}>
            <div style={{ width: `${r.progress_pct}%`, height: "100%", borderRadius: 2, background: "linear-gradient(90deg,#EFC75E,#D9A83C)" }} />
          </div>
        )}
      </div>
      <StatusChip status={r.status} />
    </div>
  );
}

function Block({ emoji, title, stats, items, accent, testid }) {
  return (
    <section data-testid={testid} style={{
      borderRadius: 18, padding: "16px 16px 8px",
      background: "rgba(255,255,255,0.045)",
      border: `1px solid ${accent}30`,
      boxShadow: `inset 0 1px 0 ${accent}18`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 18 }}>{emoji}</span>
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: 0.3, color: accent }}>{title}</h2>
      </div>
      {stats && stats.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {stats.map((s) => (
            <div key={s.label} style={{
              flex: "1 1 30%", minWidth: 90, textAlign: "center", padding: "8px 6px",
              borderRadius: 12, background: "rgba(0,0,0,0.25)", border: "1px solid rgba(255,255,255,0.07)",
            }}>
              <div style={{ fontSize: 19, fontWeight: 800, color: accent }}>{s.value}</div>
              <div style={{ fontSize: 9.5, opacity: 0.6, letterSpacing: 0.3, textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div>
        {items.length === 0 ? (
          <div style={{ fontSize: 12, opacity: 0.45, padding: "10px 0 14px" }}>Aucune demande pour l'instant.</div>
        ) : items.map((r) => <RequestRow key={r.request_id} r={r} />)}
      </div>
    </section>
  );
}

export default function DiasporaDashboardPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [dash, setDash] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async (allowSeed = true) => {
    try {
      const token = localStorage.getItem("civ_user_token");
      const r = await fetch(`${API}/api/v3/requests/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "chargement impossible");
      // Auto-seed démo au premier passage (tableau vide) — vision investisseur.
      if (allowSeed && d.dashboard.total_requests === 0) {
        await fetch(`${API}/api/v3/requests/seed-demo`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        return load(false);
      }
      setDash(d.dashboard);
    } catch (e) {
      setErr(e?.message || "erreur");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) { navigate("/login"); return; }
    load(true);
  }, [isAuthenticated, load, navigate]);

  const fam = dash?.famille;
  return (
    <div data-testid="diaspora-dashboard-page" style={{
      minHeight: "100vh", color: "#F4F1E8",
      background: "radial-gradient(1200px 600px at 20% -10%, rgba(239,199,94,0.10), transparent), #07130D",
      fontFamily: "'Inter', system-ui, sans-serif", padding: "18px 16px 60px",
    }}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <header style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <button data-testid="diaspora-back-btn" onClick={() => navigate("/")} aria-label="Retour au chat" style={{
            width: 34, height: 34, borderRadius: 10, border: "1px solid rgba(255,255,255,0.14)",
            background: "rgba(255,255,255,0.06)", color: "#F4F1E8", cursor: "pointer", fontSize: 16,
          }}>←</button>
          <div>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 2.2, color: "#EFC75E" }}>🌍 ESPACE DIASPORA</div>
            <h1 style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 800 }}>
              Akwaba {user?.display_name?.split(" ")[0] || ""} — tout ce qui compte pour toi, ici.
            </h1>
          </div>
        </header>
        <p style={{ margin: "0 0 18px 46px", fontSize: 12, opacity: 0.55 }}>
          Famille, projets, installation, solidarité : un seul moteur AYA, quatre vues.
        </p>

        {err && <div data-testid="diaspora-dashboard-error" style={{ color: "#F87171", fontSize: 13, marginBottom: 14 }}>❌ {err}</div>}
        {!dash && !err && <div data-testid="diaspora-dashboard-loading" style={{ opacity: 0.6, fontSize: 13 }}>⏳ Chargement de ton espace…</div>}

        {dash && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14 }}>
            <Block
              testid="diaspora-block-famille"
              emoji="❤️" title="Famille" accent="#F87171"
              stats={[
                { label: "En attente", value: fam?.pending ?? 0 },
                { label: "Livraison aujourd'hui", value: fam?.deliveries_today ?? 0 },
                { label: "Payées ce mois", value: fam?.paid_this_month ?? 0 },
              ]}
              items={fam?.items || []}
            />
            <Block
              testid="diaspora-block-business"
              emoji="💼" title="Business" accent="#60A5FA"
              stats={null}
              items={dash.business?.items || []}
            />
            <Block
              testid="diaspora-block-installation"
              emoji="🏠" title="Installation" accent="#EFC75E"
              stats={null}
              items={dash.installation?.items || []}
            />
            <Block
              testid="diaspora-block-solidarite"
              emoji="🕊️" title="Solidarité" accent="#6EE7B7"
              stats={[{ label: "Personnes aidées ce mois", value: dash.solidarite?.helped_this_month ?? 0 }]}
              items={dash.solidarite?.items || []}
            />
          </div>
        )}

        <div style={{ marginTop: 22, textAlign: "center" }}>
          <button data-testid="diaspora-ask-aya-btn" onClick={() => navigate("/")} style={{
            padding: "12px 26px", borderRadius: 999, border: "none", cursor: "pointer",
            background: "linear-gradient(135deg,#EFC75E,#D9A83C)", color: "#1A1206", fontWeight: 800, fontSize: 13.5,
          }}>💬 Demander quelque chose à AYA</button>
        </div>
      </div>
    </div>
  );
}
